import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirstAvailableRoute } from '../utils/routeUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSystemAdmin?: boolean;
  requireTenantAdmin?: boolean;
  permission?: string;
  permissions?: string[]; // Any-of: user needs at least one of these permissions
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSystemAdmin,
  requireTenantAdmin,
  permission,
  permissions,
}) => {
  const { isAuthenticated, isSystemAdmin, isTenantAdmin, isTenantLevelAdmin, hasPermission, loading, user, token } = useAuth();

  const isAdmin = isSystemAdmin || isTenantAdmin || isTenantLevelAdmin;

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '20px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Check authentication - use both isAuthenticated and direct checks for reliability
  const actuallyAuthenticated = isAuthenticated || (!!token && !!user);
  
  
  if (!actuallyAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireSystemAdmin && !isSystemAdmin) {
    const firstRoute = getFirstAvailableRoute(hasPermission, isSystemAdmin, isTenantAdmin);
    return <Navigate to={firstRoute} replace />;
  }

  if (requireTenantAdmin && !isAdmin) {
    const firstRoute = getFirstAvailableRoute(hasPermission, isSystemAdmin, isTenantAdmin);
    return <Navigate to={firstRoute} replace />;
  }

  if (permission) {
    const blockedTenantSettings =
      isTenantLevelAdmin &&
      (permission === 'tenant.update' || permission === 'tenant.create' || permission === 'tenant.delete');
    if (blockedTenantSettings || !(isAdmin || hasPermission(permission))) {
      const firstRoute = getFirstAvailableRoute(hasPermission, isSystemAdmin, isTenantAdmin);
      return <Navigate to={firstRoute} replace />;
    }
  }

  if (permissions && permissions.length > 0 && !(isAdmin || permissions.some((p) => hasPermission(p)))) {
    const firstRoute = getFirstAvailableRoute(hasPermission, isSystemAdmin, isTenantAdmin);
    return <Navigate to={firstRoute} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

